import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getQueryFn } from '../lib/queryClient';
import { useAuth } from '../hooks/use-auth';

// Types for Year Context
export interface YearContextType {
  baseYear: number | null;
  selectedYear: number | null;
  comparisonYear: number | null;
  availableYears: number[];
  setSelectedYear: (year: number | null) => void;
  setComparisonYear: (year: number | null) => void;
  setCurrentCompany: (company: any | null) => void;
  toggleComparisonMode: () => void;
  isComparisonMode: boolean;
  currentCompany: any | null;
  companies: any[] | null;
  isLoading: boolean;
}

// Create the context
const YearContext = createContext<YearContextType | undefined>(undefined);

// Provider component
interface YearContextProviderProps {
  children: ReactNode;
}

export function YearContextProvider({ children }: YearContextProviderProps) {
  const { user } = useAuth();
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [comparisonYear, setComparisonYear] = useState<number | null>(null);
  const [currentCompany, setCurrentCompany] = useState<any | null>(null);
  const [isComparisonMode, setIsComparisonMode] = useState<boolean>(false);

  // Fetch user's companies
  const { data: companies, isLoading: companiesLoading } = useQuery({
    queryKey: ['/api/users/me/companies'],
    queryFn: getQueryFn(),
    enabled: !!user,
  });

  // Auto-select first company if none selected
  useEffect(() => {
    if (companies && companies.length > 0 && !currentCompany) {
      const preferred = companies.find((c: any) => c.id === 1) || companies[0];
      setCurrentCompany(preferred);
    }
  }, [companies, currentCompany]);

  // Set reporting year to base year when company changes
  useEffect(() => {
    if (currentCompany?.base_year) {
      setSelectedYear(currentCompany.base_year);
      setComparisonYear(null); // Reset comparison year
      setIsComparisonMode(false); // Reset comparison mode
    }
  }, [currentCompany]);

  // Generate available years (base year ± 5 years)
  const availableYears = React.useMemo(() => {
    if (!currentCompany?.base_year) return [];
    
    const baseYear = currentCompany.base_year;
    const currentYear = new Date().getFullYear();
    const years = [];
    
    // Include years from base year - 5 to current year + 1
    for (let year = Math.max(2000, baseYear - 5); year <= currentYear + 1; year++) {
      years.push(year);
    }
    
    return years.sort((a, b) => b - a); // Most recent first
  }, [currentCompany]);

  // Toggle comparison mode
  const toggleComparisonMode = () => {
    setIsComparisonMode(!isComparisonMode);
    // Always use base year for comparison, no need to set comparisonYear
  };

  const contextValue: YearContextType = {
    baseYear: currentCompany?.base_year || null,
    selectedYear,
    comparisonYear,
    availableYears,
    setSelectedYear,
    setComparisonYear,
    setCurrentCompany,
    toggleComparisonMode,
    isComparisonMode,
    currentCompany,
    companies,
    isLoading: companiesLoading,
  };

  return (
    <YearContext.Provider value={contextValue}>
      {children}
    </YearContext.Provider>
  );
}

// Custom hook to use the Year Context
export function useYearContext(): YearContextType {
  const context = useContext(YearContext);
  if (context === undefined) {
    throw new Error('useYearContext must be used within a YearContextProvider');
  }
  return context;
}

// Helper hook for getting year context with company selection
export function useYearContextWithCompany(companyId?: number) {
  const context = useYearContext();
  const { data: companies } = useQuery({
    queryKey: ['/api/users/me/companies'],
    queryFn: getQueryFn(),
  });

  const targetCompany = React.useMemo(() => {
    if (companyId && companies) {
      return companies.find((c: any) => c.id === companyId);
    }
    return context.currentCompany;
  }, [companyId, companies, context.currentCompany]);

  return {
    ...context,
    currentCompany: targetCompany,
    baseYear: targetCompany?.base_year || null,
  };
}
